import React, { ReactNode } from 'react'
import type { RenderResult } from '@testing-library/react'
import { render as rtlRender, screen, waitFor } from '@testing-library/react'
import { configureStore, Store } from '@reduxjs/toolkit'
import { Provider } from 'react-redux'
import userEvent from '@testing-library/user-event'
import reducer, { ReduxState } from '../store/reducers'
import { GeoEntity } from '../@types/geoEntity'
import * as geoClient from '../clients/geoClient'
import { defaultGeoEntity } from '../fixtures/geoEntity'
import { FeatureName } from '../featureToggles/featureToggles'
import * as featureToggles from '../featureToggles/featureToggles'

type InitialState = ReduxState | Record<string, never>
type rgb = { r: number; g: number; b: number }

export const renderWithStore = (component: ReactNode, initialState: InitialState = {}): RenderResult =>
  render(component, withState(initialState))

const decorate = (node: ReactNode, ...decorators: ReactNode[]): ReactNode =>
  decorators
    ? decorators.reduce((decorated, decorator) => typeof decorator === 'function' && decorator(decorated), node)
    : node

export const render = (node: ReactNode, ...decorators: ReactNode[]) => {
  const { rerender, ...otherProps } = rtlRender(<>{decorate(node, ...decorators)}</>)
  return {
    ...otherProps,
    rerender: (node: ReactNode) => rerender(<>{decorate(node, ...decorators)}</>),
  }
}

export const configureMockStore = (initialState = {}) =>
  configureStore({
    reducer,
    devTools: false,
    preloadedState: initialState,
  })

export const withState = (state = {}) => withStore(configureMockStore(state))

// eslint-disable-next-line react/display-name
export const withStore = (store: Store) => (node: ReactNode) => <Provider store={store}>{node}</Provider>

export const convertHexToRGBA = (hexCode: string): rgb => {
  let hex = hexCode.replace('#', '')
  if (hex.length === 3) {
    hex = `${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`
  }
  const r = parseInt(hex.substring(0, 2), 16)
  const g = parseInt(hex.substring(2, 4), 16)
  const b = parseInt(hex.substring(4, 6), 16)

  return { r, g, b }
}

export const generateRGBAString = (hexCode: string, opacity?: number): string | undefined => {
  if (!hexCode) return undefined
  const { r, g, b } = convertHexToRGBA(hexCode)
  return opacity ? `rgb(${r} ${g} ${b}/ ${opacity}%)` : `rgb(${r} ${g} ${b})`
}

export const selectValidPostcode = async (validPostcode?: string) => {
  const VALID_POSTCODE: string = validPostcode ?? '10243'
  const SLICED_POSTCODE = VALID_POSTCODE.slice(0, 3)
  const VALID_LOCATION_OPTION_ID = `location-option-P276-${VALID_POSTCODE}-BERLIN`
  const postcodes = [
    {
      entity: {
        id: 'P276-10245-BERLIN',
        label: '10245 Berlin',
        value: '10245',
      } as GeoEntity,
    },
    {
      entity: {
        id: `P276-${VALID_POSTCODE}-BERLIN`,
        label: `${VALID_POSTCODE} Berlin`,
        value: VALID_POSTCODE,
      } as GeoEntity,
    },
  ]
  jest.spyOn(geoClient, 'gacApiFetch').mockResolvedValue(postcodes)
  jest.spyOn(geoClient, 'getGeoEntity').mockResolvedValue({
    ...defaultGeoEntity,
    value: VALID_POSTCODE,
    address: {
      ...defaultGeoEntity.address,
      postcode: VALID_POSTCODE,
    },
  })

  const locationInput = screen.getByTestId('location-combobox')
  userEvent.type(locationInput, SLICED_POSTCODE)
  expect(locationInput).toHaveValue(SLICED_POSTCODE)
  await screen.findByTestId(VALID_LOCATION_OPTION_ID)
  userEvent.click(screen.getByTestId(VALID_LOCATION_OPTION_ID))
  await waitFor(() => expect(locationInput).toHaveValue(VALID_POSTCODE))
}

export const activateFeatures = (...featureNames: FeatureName[]) => {
  jest
    .spyOn(featureToggles, 'getFeatureToggleValue')
    .mockImplementation((featureName: FeatureName) => featureNames.includes(featureName))
}
